/**
 * F23 (Wave 2C) — admin "mark statement settled" modal.
 *
 * Composes Modal + FormField + TextInput (reference 1–120) + Textarea
 * (notes ≤ 500 optional). Mirrors F20's mark-as-paid Modal pattern.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button, FormField, Modal, Textarea, TextInput } from '@bravemove/ui-web';
import { MarkStatementSettledInputSchema, type MarkStatementSettledInput } from '@bravemove/types';

export interface MarkSettledDialogProps {
  readonly open: boolean;
  readonly statementId: string;
  readonly onConfirm: (input: MarkStatementSettledInput) => Promise<void>;
  readonly onClose: () => void;
}

export function MarkSettledDialog({
  open,
  statementId,
  onConfirm,
  onClose,
}: MarkSettledDialogProps) {
  const { t } = useI18n();
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReference('');
    setNotes('');
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: MarkStatementSettledInput = useMemo(() => {
    const base: MarkStatementSettledInput = { settledReference: reference.trim() };
    if (notes.trim().length > 0) base.settledNotes = notes.trim();
    return base;
  }, [reference, notes]);

  const validation = useMemo(() => MarkStatementSettledInputSchema.safeParse(payload), [payload]);

  const submit = async () => {
    if (!validation.success) return;
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } catch {
      // CST-008 — the page's onConfirm surfaces the failure via a danger toast
      // and re-throws; swallow here so it never escapes `void submit()` as an
      // unhandled rejection and the dialog stays OPEN for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  const disableSubmit = submitting || !validation.success;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:commission.dialog.mark_settled.title', {
        defaultValue: 'Mark statement settled',
      })}
      description={t('admin:commission.dialog.mark_settled.description', {
        defaultValue: 'Confirm the venue has paid this statement off-platform.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="mark-settled-cancel"
          >
            {t('admin:commission.dialog.mark_settled.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="mark-settled-confirm"
          >
            {t('admin:commission.dialog.mark_settled.confirm', { defaultValue: 'Mark settled' })}
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-4"
        data-testid="mark-settled-dialog"
        data-statement-id={statementId}
      >
        <FormField
          label={t('admin:commission.dialog.mark_settled.reference_label', {
            defaultValue: 'Reference',
          })}
          helper={t('admin:commission.dialog.mark_settled.reference_helper', {
            defaultValue: 'e.g. Whish transaction id, bank reference (1–120 chars).',
          })}
          required
        >
          <TextInput
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            data-testid="mark-settled-reference"
            maxLength={120}
            aria-label={t('admin:commission.dialog.mark_settled.reference_label', {
              defaultValue: 'Reference',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:commission.dialog.mark_settled.notes_label', {
            defaultValue: 'Notes (optional)',
          })}
        >
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="mark-settled-notes"
          />
        </FormField>
      </div>
    </Modal>
  );
}

export default MarkSettledDialog;
